import axios, { type AxiosResponse } from "axios";
import { type AccountAvailablePackOverview } from "features/Accounts/PackAssignmentModal/types";
import { type SortingDirection } from "enums";
import {
  type AccountInfoResponse,
  type AccountDependenciesResponse,
  type CreateAccountRequest,
  type LimitedAccountInfoResponse,
  type UpdateLimitedAccountInfoRequest,
} from "features/Accounts/services/types";
import type { GetPacksRequest } from "features/Licensing/Packs/types/requests";
import { type ResponseWithCancellation } from "features/Licensing/Packs/types/services";
import { packFilterTypes } from "features/Licensing/Packs/types/urlParams";
import { type PageOfItems } from "interfaces";
import { type Account, type AutocompleteAccount } from "interfaces/Account";
import { type BatchResponse } from "interfaces/batchResponse";
import { type AddContactPayload } from "interfaces/contact";
import { type GetPagedDataRequestV2 } from "interfaces/getPagedDataRequest";
import { type GlobalRole } from "interfaces/role";
import { isEmpty } from "lodash";
import { type DistributedOperationResult } from "types";
import { formatFilters } from "utils/filterMapUtils";
import { type Filters, overviewFiltering, serializeArrayAndFilterNullable } from "utils/queryUtils";
import StatusCode from "../../../enums/httpStatusCodes";
import type Contact from "../../../interfaces/contact";
import type Role from "../../../interfaces/role";
import http, { type HttpResponse } from "../../../lib/http";
import KnownHttpHeaders from "../../Application/services/knownHttpHeaders";
import { type TemplateOverviewResponse } from "../../People/GroupTemplate/DataService/types";
import {
  type AccountType,
  type AddOn,
  type ContractType,
  type EditSystemNotificationSettingsRequest,
  type EmailSetting,
  type ParentAccount,
  type SavingAddOn,
  type Subscription,
  type SystemNotificationSettings,
  type VerifyEmailSettingsRequest,
} from "../types";

const apiRoot = "/api/accounts";
const ownApiRoot = "/api/account";
const accountTypeRoot = "/api/account-types";

const addStaticValues = (items: any) => items.map((item: any) => ({ ...item, licensesCount: 10000 }));

interface AccountDataService {
  getAccounts(
    skip?: number,
    top?: number,
    orderProp?: string,
    orderDirection?: SortingDirection,
    searchText?: string,
    filter?: Record<string | number, unknown>,
    accountIds?: number[],
  ): Promise<{ items: Array<Account>; itemsCount: number }>;

  getAutocomplete(searchTerm: string, signal: AbortSignal): Promise<Array<AutocompleteAccount>>;

  getAccountInfo(id: number): Promise<AccountInfoResponse>;

  getAccountDependencies(id: number): Promise<AccountDependenciesResponse>;

  getLimitedAccountInfo(id: number): Promise<LimitedAccountInfoResponse>;

  getOwnAccountInfo(): Promise<HttpResponse<AccountInfoResponse>>;

  getOwnAccountBsiTags(): Promise<HttpResponse<string[]>>;

  isUniqueName(name: string): Promise<boolean>;

  isAccountAliasExists(alias: string): Promise<boolean>;

  createAccount(account: CreateAccountRequest): Promise<DistributedOperationResult>;

  addManagedByContacts(payload: AddContactPayload): Promise<DistributedOperationResult>;

  getAccountTypes(): Promise<Array<AccountType>>;

  getFilterOptions(): Promise<Array<AccountType>>;

  getAllAccountTypes(): Promise<Array<AccountType>>;

  getContractTypes(): Promise<Array<ContractType>>;

  getReplacementAccountTypes(accountId: number): Promise<Array<AccountType>>;

  getAccountTypeRoles(accountTypeId: number): Promise<GlobalRole[]>;

  getAccountsTags(includeBsi: boolean): Promise<string[]>;

  getChildAccountRoles(accountId: number): Promise<Role[]>;

  getPermissions(): Promise<any>;

  getAvailableAccounts(): Promise<Array<Account>>;

  getParentAccounts(accountTypeId: number, childAccountId?: number): Promise<ParentAccount[]>;

  getAccountAreas(): Promise<any>;

  getAccountContacts(
    accountId: number,
    skip: number,
    top: number,
    orderByParams: string,
    filterParams: string,
  ): Promise<PageOfItems<Contact>>;

  updateOwnAccount(data: any): Promise<any>;

  updateAccountInfo(accountId: number, data: any): Promise<any>;

  updateLimitedAccountInfo(accountId: number, data: UpdateLimitedAccountInfoRequest): Promise<string>;

  addAccountContacts(accountId: number, userIds: any): Promise<any>;

  updateAccountContacts(accountId: number, data: any): Promise<any>;

  getAddOns(accountId: number): Promise<Array<AddOn>>;

  updateAddOns(accountId: number, addOns: Array<SavingAddOn>): Promise<string>;

  getSubscriptionAgreement(accountId: number): Promise<Subscription>;

  deleteAccount(accountId: number): Promise<string>;

  getFlowsCountUsedInPack(accountId: number, packId: number): Promise<number>;

  removePackFromAccount(accountId: number, packId: number): Promise<string>;

  getEmailSettings(accountId: number): Promise<EmailSetting>;

  createEmailSettings(settings: EmailSetting): Promise<any>;

  updateEmailSettings(settings: EmailSetting): Promise<any>;

  deleteEmailSetting(accountId: number): Promise<any>;

  verifyEmailSettings(verifyRequest: VerifyEmailSettingsRequest): Promise<any>;

  getSmtpSenderSettings(accountId: number): Promise<any>;

  removeUsersFromAccount(accountId: number, userIds: number[]): Promise<any>;

  addTemplates(accountId: number, templateIds: number[], autoApprove: boolean): Promise<BatchResponse>;

  getTemplates(
    accountId: number,
    params: GetPagedDataRequestV2,
  ): Promise<{
    items: TemplateOverviewResponse[];
    count: number;
  }>;

  deleteTemplate(
    templateId: number,
    { accountIds }: { accountIds: string[] },
  ): Promise<AxiosResponse<{ messageIds: string[] }>>;

  getSystemNotificationSettings(accountId: number): Promise<SystemNotificationSettings>;

  updateSystemNotificationSettings(accountId: number, payload: EditSystemNotificationSettingsRequest): Promise<void>;

  isUniqueTemplateNameExists(name: string): Promise<boolean>;

  getAvailablePacksWithCancel: (
    assignedAccountId: number,
    data: GetPacksRequest,
    filters: Filters,
  ) => ResponseWithCancellation<AccountAvailablePackOverview>;

  getCompanyNames(accountId: number): Promise<AxiosResponse<string[]>>;
}

const service: AccountDataService = {
  async getAccounts(
    skip: number,
    top: number,
    orderProp: string,
    orderDirection: SortingDirection,
    searchText?: string,
    filter?: Record<string | number, unknown>,
    accountIds?: number[],
  ) {
    const config = {
      params: {
        top,
        skip,
        sortBy: `${orderProp}`,
        sortOrder: `${orderDirection}`,
        searchTerm: !isEmpty(searchText) ? searchText : undefined,
        accountIds,
        ...{ ...filter },
      },
      paramsSerializer: overviewFiltering,
    };

    const response = await axios.get(`/api/v2/accounts/linked`, config);

    return {
      items: addStaticValues(response.data.items),
      itemsCount: response.data.count,
    };
  },

  async getAutocomplete(searchTerm: string, signal: AbortSignal) {
    const { data } = await axios.get<{ items: AutocompleteAccount[] }>(`${apiRoot}/autocomplete`, {
      params: {
        searchTerm,
      },
      signal,
    });

    return data.items;
  },

  async getAccountInfo(id: number) {
    const { data } = await axios.get(`${apiRoot}/${id}`);

    return data;
  },

  async getAccountDependencies(id: number) {
    const { data } = await axios.get<AccountDependenciesResponse>(`${apiRoot}/${id}/dependencies`);

    return data;
  },

  async getLimitedAccountInfo(id: number) {
    const { data } = await axios.get(`${apiRoot}/${id}/limited`);

    return data;
  },

  async getOwnAccountInfo() {
    const response = await http.get<AccountInfoResponse>(ownApiRoot);

    return response;
  },

  async getOwnAccountBsiTags() {
    const response = await http.get<string[]>(`${apiRoot}/bsi-tags`);
    return response;
  },

  async getSubscriptionAgreement(accountId: number) {
    const response = await axios.get(`/api/contracts/subscription-agreement/${accountId}`);

    return response.data;
  },

  async isUniqueName(name: string) {
    const response = await http.head(`${apiRoot}/${name}`);
    return !response.isSuccess && response.code === StatusCode.NotFound;
  },

  async isAccountAliasExists(alias: string) {
    const encodedAlias = encodeURIComponent(alias);
    try {
      await axios.head(`${apiRoot}/aliases/${encodedAlias}`);
      return true;
    } catch (error: any) {
      if (error.response?.status === StatusCode.NotFound) {
        return false;
      }
      throw error;
    }
  },

  async createAccount(account: any) {
    const body = { ...account, sendDefaultNotificationToMainContact: false };
    const response = await axios.post<DistributedOperationResult>(apiRoot, body);
    return response.data;
  },

  addManagedByContacts(payload: AddContactPayload) {
    return axios.post("/api/accounts", payload);
  },

  async getReplacementAccountTypes(accountId: number) {
    const accountTypes = await axios.get(`${apiRoot}/${accountId}/account-types`);
    return accountTypes.data;
  },

  async getAccountTypes() {
    const accountTypes = await axios.get(`${ownApiRoot}/account-types`);
    return accountTypes.data;
  },

  async getFilterOptions() {
    const accountTypes = await axios.get(`${accountTypeRoot}/filter-options`);
    return accountTypes.data;
  },

  async getAllAccountTypes() {
    const accountTypes = await axios.get(accountTypeRoot);
    return accountTypes.data;
  },

  async getContractTypes() {
    const contractTypes = await axios.get("/api/contracts/types");
    return contractTypes.data;
  },

  async getAccountTypeRoles(accountTypeId: number) {
    const { data: roles } = await axios.get<GlobalRole[]>(`/api/account-types/${accountTypeId}/roles`);
    return roles;
  },

  async getChildAccountRoles(accountId: number) {
    const response = await axios.get<Role[]>(`/api/accounts/${accountId}/roles`);
    return response.data;
  },

  async getPermissions() {
    const permissions = await axios.get(`${apiRoot}/permissions`);
    return permissions.data;
  },

  async getAvailableAccounts() {
    const accounts = await axios.get("/api/permissions/available-accounts");
    return accounts.data;
  },

  async getParentAccounts(accountTypeId: number, childAccountId?: number) {
    const response = await axios.get(`${accountTypeRoot}/${accountTypeId}/parents`, {
      params: {
        childAccountId,
      },
    });
    return response.data;
  },

  async getAccountAreas() {
    const response = await axios.get("/api/areas");
    return response.data;
  },

  async getAccountContacts(accountId: number, skip: number, top: number, orderByParams: string, filterParams: string) {
    const config: any = { params: { skip, top } };

    if (orderByParams) {
      config.params.orderBy = orderByParams;
    }

    if (filterParams) {
      config.params.filter = filterParams;
    }

    const response = await axios.get(`${apiRoot}/${accountId}/contacts`, config);
    return {
      items: response.data,
      count: parseInt(response.headers[KnownHttpHeaders.RecordsCount]),
    };
  },

  async updateOwnAccount(data: any) {
    const response = await axios.put(`${apiRoot}/info`, data);
    return response.data;
  },

  async updateAccountInfo(accountId: number, data: any) {
    const response = await axios.put(`${apiRoot}/${accountId}/info`, {
      ...data,
      companyName: data.name,
    });
    return response.data;
  },

  async updateLimitedAccountInfo(accountId: number, data: UpdateLimitedAccountInfoRequest) {
    const response = await axios.put(`${apiRoot}/${accountId}/limited`, data);
    return response.data;
  },

  async addAccountContacts(accountId: number, userIds: any) {
    const response = await axios.post(`${apiRoot}/${accountId}/contacts`, userIds);
    return response.data;
  },

  async updateAccountContacts(accountId: number, data: any) {
    const response = await axios.put(`${apiRoot}/${accountId}/contacts`, data);
    return response.data;
  },

  async getAddOns(accountId: number) {
    const response = await axios.get(`${apiRoot}/${accountId}/addons`);
    return response.data;
  },

  async updateAddOns(accountId: number, addOns: Array<SavingAddOn>) {
    const response = await axios.post(`${apiRoot}/${accountId}/addons`, { addOns });
    return response.data;
  },

  async deleteAccount(accountId: number) {
    const response = await axios.delete(`${apiRoot}/${accountId}`);
    return response.data;
  },

  async getFlowsCountUsedInPack(accountId: number, packId: number) {
    const response = await axios.get(`${apiRoot}/${accountId}/purchased-flows/count?packId=${packId}`);
    return response.data;
  },

  async removePackFromAccount(accountId: number, packId: number) {
    const response = await axios.delete(`${apiRoot}/${accountId}/packs/${packId}`);
    return response.data;
  },

  async getEmailSettings(accountId: number) {
    const response = await axios.get(`${apiRoot}/${accountId}/smtp-settings`);
    if (response.data && response.data.length) {
      return response.data[0];
    }

    return null;
  },

  async createEmailSettings(settings: EmailSetting) {
    const response = await axios.post(`${apiRoot}/${settings.accountId}/smtp-settings`, settings);
    return response.data;
  },

  async updateEmailSettings(settings: EmailSetting) {
    const response = await axios.put(`${apiRoot}/${settings.accountId}/smtp-settings/${settings.id}`, settings);
    return response.data;
  },

  async deleteEmailSetting(accountId: number) {
    const response = await axios.delete(`${apiRoot}/${accountId}/smtp-settings`);
    return response.data;
  },

  async verifyEmailSettings(verifyRequest: VerifyEmailSettingsRequest) {
    const response = await axios.post("/api/smtp/verify", verifyRequest);
    return response.data;
  },

  async getSmtpSenderSettings(accountId: number) {
    const response = await axios.get(`${apiRoot}/${accountId}/smtp-settings/sender`);
    return response.data;
  },

  async removeUsersFromAccount(accountId: number, userIds: number[]) {
    const data = {
      ids: userIds,
    };
    return axios.delete(`${apiRoot}/${accountId}/users`, { data });
  },

  async addTemplates(accountId: number, templateIds: number[], autoApprove: boolean): Promise<BatchResponse> {
    const response = await axios.post(`/api/accounts/${accountId}/template-groups`, {
      templateGroupIds: templateIds,
      autoApprove,
    });
    return response.data;
  },

  async getTemplates(accountId: number, params: GetPagedDataRequestV2) {
    const { data, headers } = await axios.get<TemplateOverviewResponse[]>(`/api/accounts/${accountId}/templates`, {
      params,
    });
    return {
      items: data,
      count: parseInt(headers[KnownHttpHeaders.RecordsCount]),
    };
  },

  deleteTemplate(id: number, data: { accountIds: string[] }) {
    return axios.delete(`/api/template-groups/${id}/accounts`, { data });
  },

  async getAccountsTags(includeBsi: boolean) {
    const { data } = await axios.get<string[]>(`/api/accounts/tags?includeBsi=${includeBsi}`);

    return data;
  },

  async getSystemNotificationSettings(accountId: number) {
    const response = await axios.get(`${apiRoot}/${accountId}/notifications/settings`);
    return response.data;
  },

  updateSystemNotificationSettings(accountId: number, payload: EditSystemNotificationSettingsRequest) {
    return axios.put(`${apiRoot}/${accountId}/notifications/settings`, payload);
  },

  async isUniqueTemplateNameExists(name: string) {
    const response = await http.head(`api/templates/${name}`);
    return !response.isSuccess && response.code === StatusCode.NotFound;
  },

  getAvailablePacksWithCancel(assignedAccountId: number, data: GetPacksRequest, filters: Filters) {
    const source = axios.CancelToken.source();

    const config = {
      params: data,
      paramsSerializer: serializeArrayAndFilterNullable,
      cancelToken: source?.token,
    };

    const apiUrl = `/api/v2/accounts/${assignedAccountId}/available-packs`;
    const formattedFilters = formatFilters(filters, packFilterTypes);

    config.params = { ...config.params, ...formattedFilters };

    return { getDataPromise: axios.get<AccountAvailablePackOverview[]>(apiUrl, config), cancel: source.cancel };
  },
  async getCompanyNames(accountId: number) {
    return axios.get(`/api/accounts/${accountId}/company-names`);
  },
};

export default service;
